import UserProcessMasterProxy from "../../utility/proxy/manager/manufacturingProxy/userProcessMasterProxy";

/****************************************************
 * Module Name:  UserProcessMaster.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";
import Constants from "../../utility/constants";
import { Box, Tooltip, Button, CircularProgress, Chip, Avatar } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import renderCellExpand from "../common/dataGridCellExpand";
import CommonSnackbar from "../common/commonSnackBar";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import Utility from "../../utility/utility";
import AddMfgUserProcessModal from "./addMfgUserProcessModal";

const UserProcessMaster = () => {
    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [userProcessList, setUserProcessList] = useState([]);
    const [access, setAccess] = useState(false);
    const [userProcessListLoader, setUserProcessListLoader] = useState(false);
    const [sourceData, setSourceData] = useState([]);
    const [editOperation, setEditOperation] = useState(false);
    const [userProcessSuccessSnackbar, setUserProcessSuccessSnackbar] = useState(false);
    const [showAddUserProcessModal, setShowAddUserProcessModal] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [userProcessOperation, setUserProcessOperation] = useState('create');

    useEffect(() => {
        PageTitle("MANUFACTURING_MASTER_USERPROCESS");
        checkPermission();
    }, []);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.MANUFACTURING_MASTER_USERPROCESS
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        } else {
            handleSearch();
        }
    }

    const handleSearch = () => {
        setUserProcessListLoader(true);
        UserProcessMasterProxy.GetAllUserProcess((responseData) => {
            if (responseData) {
                setUserProcessList(responseData);
                setUserProcessListLoader(false);
            } else {
                setUserProcessListLoader(false);
                setErrorSnackbar(true);
            }
        });
    }


    const handleClose = () => {
        setUserProcessSuccessSnackbar(false);
    };

    const addOrUpdateUserProcess = (userid, processid) => {
        setLoadingButton(true);
        UserProcessMasterProxy.createOrUpdateUserProcess(userid, processid, (response) => {
            if (response) {
                setLoadingButton(false);
                handleSearch();
                setUserProcessSuccessSnackbar(true);
                setUserProcessOperation(editOperation ? 'update' : 'create');
                setShowAddUserProcessModal(false);
            }
            else {
                setLoadingButton(false);
                setErrorSnackbar(true);
            }
        });
    }

    const initialColumns = () => [
        {
            field: "userid",
            headerName: "USERID",
            headerAlign: "center",
            align: "center",
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "username",
            headerName: "USERNAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "firstname",
            headerName: "FIRST NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "lastname",
            headerName: "LAST NAME",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "email",
            headerName: "EMAIL",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "phone",
            headerName: "PHONE",
            headerAlign: "center",
            align: "center",
            flex: 1,
            renderCell: renderCellExpand,
        },
        {
            field: "assignedprocess",
            headerName: "ASSIGNED PROCESS",
            headerAlign: "center",
            align: "center",
            flex: 3,
            renderCell: (params) => (
                <Box sx={{ minHeight: "auto", mt: 1 }}>
                    {params.value.map((process, index) => (
                        <Chip
                            key={index}
                            avatar={<Avatar>{process.id}</Avatar>}
                            label={process.name}
                            sx={{ margin: "0px 10px 10px 0px" }}
                        />
                    ))}
                </Box>
            ),
            sortComparator: (v1, v2) => Utility.sortOperatorFn(v1, v2, "name")
        },
        {
            field: "edit",
            headerName: "EDIT",
            headerAlign: "center",
            align: "center",
            flex: 0.5,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const rowData = { ...params.row };
                return (
                    <Box
                        style={{ textAlign: "center", cursor: "pointer" }}
                        onClick={() => {
                            setEditOperation(true)
                            setShowAddUserProcessModal(true)
                            setSourceData(rowData)
                        }}
                    >
                        {" "}
                        <BorderColorIcon color="primary" />
                    </Box>
                );
            },
        },
    ];

    const columns = initialColumns();

    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />

            {userProcessSuccessSnackbar ? (
                <CommonSnackbar
                    show={userProcessSuccessSnackbar}
                    message={`Process ${userProcessOperation === 'update' ? 'updated' : 'created'} successfully`}
                    variant={"success"}
                    autoHide={true}
                    title={"success"}
                    handleClose={handleClose}
                />
            ) : null}

            {showAddUserProcessModal ? (
                <AddMfgUserProcessModal
                    Show={true}
                    handleClose={() => setShowAddUserProcessModal(false)}
                    handleSave={(userid, processid) => addOrUpdateUserProcess(userid, processid)}
                    editOperation={editOperation}
                    sourceData={sourceData}
                    loading={loadingButton}
                />
            ) : null}

            {
                access ? <NoPermission /> : (
                    <Box px={1} mt={4}>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                height: "85vh",
                            }}
                        >
                            <DataGridPro
                                disableRowSelectionOnClick={true}
                                density="compact"
                                loading={userProcessListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: GridToolbar }}
                                rows={userProcessList}
                                columns={columns}
                                getRowId={(row) => row.userid}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            userid: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                            />
                            <Box
                                style={{
                                    position: "absolute",
                                    top: 5,
                                    right: "270px",
                                    display: "flex",
                                    flexWrap: "nowrap",
                                    alignItems: "center"
                                }}>
                                {userProcessListLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            color="primary"
                                            fontSize="medium"
                                            onClick={() => handleSearch()}
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                                <Tooltip title="Add Consignee">
                                    <Button
                                        color="info"
                                        variant="outlined"
                                        size="small"
                                        style={{ marginRight: "10px", marginLeft: "10px" }}
                                        onClick={() => {
                                            setEditOperation(false)
                                            setShowAddUserProcessModal(true)
                                            setSourceData([])
                                        }
                                        }
                                        startIcon={<AddBoxOutlinedIcon size="large" />}
                                    >
                                        Assign
                                    </Button>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                )
            }
        </Box >
    )
};

export default UserProcessMaster;